/**
 * Shared quantity-weighted source-attribution log for `checkout_experience`.
 *
 * Tracks which UI added each unit currently in the cart, via the
 * `_experience_source_log` cart attribute — a JSON object keyed by variant
 * id, each value an array with one entry ("classic" | "chat" | "shared") per
 * unit of that variant, in add order.
 *
 * "shared" = added via a UI that exists site-wide regardless of which
 * ordering experience is being tested (the open cart drawer's own line-item
 * controls, product-page/quick-add `<product-form>`, main-collection-product-grid,
 * quiz recommendations, any other grid) — as opposed to "classic", which is
 * reserved *only* for the featured-collections grid card's own fast-add
 * stepper, the one genuine classic-vs-chat A/B surface on the classic side.
 * "shared" entries are intentionally never counted in classicTotal/chatTotal
 * below, so they don't influence the `checkout_experience` majority vote, but
 * they still count toward a variant's tracked array length (so they aren't
 * miscounted as untracked).
 *
 * Consumed by:
 *  - assets/checkout-experience.js (classic checkout entry point)
 *  - assets/cart-drawer-ie.js (featured-collections grid + shared drawer/grid mutations)
 *  - assets/product-form.js (product-page hero + quick-add)
 *  - wonderful-cart/utils/sessionCartSync.ts (chat checkout + sync entry points)
 *  - wonderful-cart/hooks/useShopifyCart.ts (chat mutations)
 *
 * Optionally uses assets/wonderful-cart-log-shared.js (should load first, but falls back to a local
 * implementation if it didn't) for the cart-URL/fetch/live-quantity helpers shared with the sibling
 * wonderful-section-source-log.js. Must itself load before all of the consumers above; either
 * sibling log can load before the other.
 */
;(function () {
  var ATTR_KEY = '_experience_source_log'

  /** In-memory mirror of the persisted log. Null until first hydrated from /cart.js. */
  var state = null
  var hydrating = null
  /** True when `state` has local changes not yet confirmed written to Shopify. */
  var dirty = false

  // resolveCartUrl/fetchCart/liveQuantitiesFromCart were byte-identical between this file and its
  // sibling wonderful-section-source-log.js — now shared via assets/wonderful-cart-log-shared.js
  // (should load first). fetchCart() in particular collapses concurrent callers (this module and its
  // sibling both fetching /cart.js around the same tick, e.g. checkout-experience.js's Promise.all)
  // into a single request instead of each firing its own. Falls back to a local, self-contained
  // implementation if the shared script didn't load (CDN hiccup, blocked request, script reordered)
  // rather than throwing — this module worked standalone before the shared file existed, and losing
  // the fetch-dedupe optimization is a much smaller problem than breaking recordDelta/hydrate outright.
  function resolveCartUrlFallback(path) {
    var root =
      typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root
        ? String(Shopify.routes.root).replace(/\/?$/, '/')
        : '/'
    return root + path
  }

  function resolveCartUrl(path) {
    return window.WonderfulCartLogShared
      ? window.WonderfulCartLogShared.resolveCartUrl(path)
      : resolveCartUrlFallback(path)
  }

  function fetchCart() {
    if (window.WonderfulCartLogShared) return window.WonderfulCartLogShared.fetchCart()
    return fetch(resolveCartUrlFallback('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('GET /cart.js failed: ' + res.status)
      return res.json()
    })
  }

  function parseLog(cart) {
    var raw = cart && cart.attributes && cart.attributes[ATTR_KEY]
    if (!raw) return {}
    try {
      var parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (e) {
      return {}
    }
  }

  /**
   * Fire-and-forget write-back for stale entries pruned during hydrate (not tied to
   * `dirty`/markFlushed). Only ever POSTs this module's own attribute key.
   *
   * KNOWN LIMITATION: this races the sibling log's own writes (persistPrunedLog here and in
   * wonderful-section-source-log.js, plus each module's own recordDelta-triggered flush) — if two
   * POSTs to /cart/update.js land out of send order, whichever completes last on the server wins for
   * whatever key(s) it carries. An earlier attempt to close this by having each prune-write also
   * carry a snapshot of the sibling's current in-memory attribute made this *worse*, not better: the
   * snapshot could itself be staler than a legitimate write already in flight from the sibling,
   * silently reverting it. A real fix needs either a confirmed atomic per-key merge on Shopify's end
   * (in which case there's no race to fix here at all) or genuine request sequencing — bigger in
   * scope than a client-side patch merge, and not worth guessing at without being able to verify
   * against the live cart API. Left as-is deliberately; do not "fix" this with a sibling-state merge.
   */
  function persistPrunedLog() {
    fetch(resolveCartUrl('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: buildAttributePatch() }),
    }).catch(function () {})
  }

  /**
   * Resolves once `state` reflects the persisted cart attribute (fetched at
   * most once). Also reconciles against live quantities right away — not just
   * at checkout — and prunes+persists immediately if anything was stale (e.g.
   * a completed purchase reused the same cart, or units were removed via a
   * path we don't instrument). This doesn't try to guess *why* the cart is in
   * whatever state it's in; it just re-applies the same "trim to live qty"
   * rule reconcileAgainstLive already uses, proactively instead of lazily.
   */
  function hydrate() {
    if (state) return Promise.resolve(state)
    if (hydrating) return hydrating
    hydrating = fetchCart()
      .then(function (cart) {
        var parsed = parseLog(cart)
        var live = liveQuantitiesFromCart(cart)
        var result = reconcileAgainstLive(parsed, live)
        state = result.reconciled
        var changed = JSON.stringify(parsed) !== JSON.stringify(state)
        if (changed) persistPrunedLog()
        return state
      })
      .catch(function () {
        state = {}
        return state
      })
      .finally(function () {
        hydrating = null
      })
    return hydrating
  }

  /**
   * Records N unit adds (delta > 0, pushes `source` N times) or removals
   * (delta < 0, pops N entries off the end — LIFO, regardless of `source`).
   * `label` (e.g. product/flavor name) is for console logging only — it is
   * never persisted in the `_experience_source_log` attribute itself.
   */
  function recordDelta(variantId, delta, source, label) {
    var vid = String(variantId || '')
    var n = Math.trunc(Number(delta) || 0)
    if (!vid || n === 0) return Promise.resolve()
    return hydrate().then(function () {
      var arr = (state[vid] || []).slice()
      if (n > 0) {
        for (var i = 0; i < n; i++) arr.push(source)
      } else {
        arr = arr.slice(0, Math.max(0, arr.length + n))
      }
      if (arr.length > 0) state[vid] = arr
      else delete state[vid]
      dirty = true
    })
  }

  /**
   * Reconciles a log against live per-variant quantities: trims arrays longer
   * than live qty (untracked decrease — exact, LIFO), leaves arrays shorter
   * than live qty as-is (untracked increase — those units don't vote).
   *
   * Also computes (but never persists) an `untrackedTotal`/`perVariant`
   * breakdown purely for visibility: units with no log entry at all — added
   * via a path we don't instrument (native /cart page, a third-party app,
   * the external-tool embed, etc.) — already can't be counted as classic or
   * chat since the tally only sums entries that exist in the array, but
   * nothing surfaced *how many* or *which variant* before this.
   */
  function reconcileAgainstLive(logState, liveQuantitiesByVariant) {
    var classicTotal = 0
    var chatTotal = 0
    var untrackedTotal = 0
    var reconciled = {}
    var perVariant = {}
    var seenVariants = {}

    Object.keys(logState || {}).forEach(function (vid) {
      seenVariants[vid] = true
      var arr = (logState[vid] || []).slice()
      var live = liveQuantitiesByVariant[vid] || 0
      var trimmedStaleEntries = 0
      if (arr.length > live) {
        trimmedStaleEntries = arr.length - live
        arr = arr.slice(0, live)
      }
      var untracked = Math.max(0, live - arr.length)
      if (arr.length > 0) reconciled[vid] = arr
      var vClassic = 0
      var vChat = 0
      arr.forEach(function (source) {
        if (source === 'classic') {
          classicTotal++
          vClassic++
        } else if (source === 'chat') {
          chatTotal++
          vChat++
        }
      })
      untrackedTotal += untracked
      perVariant[vid] = {
        live: live,
        classic: vClassic,
        chat: vChat,
        untracked: untracked,
        trimmedStaleEntries: trimmedStaleEntries,
      }
    })

    // Variants live in the cart with no log entry at all (never tracked, not just under-tracked).
    Object.keys(liveQuantitiesByVariant || {}).forEach(function (vid) {
      if (seenVariants[vid]) return
      var live = liveQuantitiesByVariant[vid] || 0
      if (live <= 0) return
      untrackedTotal += live
      perVariant[vid] = { live: live, classic: 0, chat: 0, untracked: live, trimmedStaleEntries: 0 }
    })

    return {
      classicTotal: classicTotal,
      chatTotal: chatTotal,
      untrackedTotal: untrackedTotal,
      perVariant: perVariant,
      reconciled: reconciled,
    }
  }

  function liveQuantitiesFromCart(cart) {
    if (window.WonderfulCartLogShared) return window.WonderfulCartLogShared.liveQuantitiesFromCart(cart)
    var map = {}
    ;(cart.items || []).forEach(function (line) {
      var vid = String(line.variant_id)
      map[vid] = (map[vid] || 0) + (line.quantity || 0)
    })
    return map
  }

  /** `patch[ATTR_KEY] = JSON.stringify(state)` shape used by every attribute-patch getter below. */
  function buildAttributePatch() {
    var patch = {}
    patch[ATTR_KEY] = JSON.stringify(state)
    return patch
  }

  /** Async patch for a `/cart/update.js` `attributes` payload, or null if nothing pending. */
  function getPendingAttributesPatch() {
    return hydrate().then(function () {
      if (!dirty) return null
      return buildAttributePatch()
    })
  }

  /** Best-effort sync variant for keepalive/beacon-style writes that can't await hydration. */
  function getPendingAttributesPatchSync() {
    if (!state || !dirty) return null
    return buildAttributePatch()
  }

  function markFlushed() {
    dirty = false
  }

  /**
   * Wipes the entire log unconditionally — for full-cart-clear actions, where
   * every variant's quantity hits 0 regardless of what the log currently
   * tracks. Safer than reconstructing per-variant pops from the DOM, which can
   * miss variants that were never rendered/synced into the clearing UI.
   */
  function clearAll() {
    state = {}
    dirty = true
  }

  /**
   * Reconciles the log against a fresh live cart read, computes the
   * quantity-weighted majority, and returns the exact `attributes` patch
   * (log + `checkout_experience`) to send in a single `/cart/update.js` call.
   */
  function computeCheckoutAttributes() {
    return hydrate()
      .then(function () {
        return fetchCart()
      })
      .then(function (cart) {
        var live = liveQuantitiesFromCart(cart)
        var result = reconcileAgainstLive(state, live)
        state = result.reconciled
        dirty = false
        var majority =
          result.classicTotal > result.chatTotal
            ? 'classic'
            : result.chatTotal > result.classicTotal
            ? 'chat'
            : null
        var attributes = buildAttributePatch()
        attributes.checkout_experience = majority || ''
        return { attributes: attributes, majority: majority }
      })
      .catch(function (e) {
        console.warn('[Wonderful Experience Log] computeCheckoutAttributes failed', e)
        return { attributes: { checkout_experience: '' }, majority: null }
      })
  }

  window.WonderfulExperienceLog = {
    recordDelta: recordDelta,
    getPendingAttributesPatch: getPendingAttributesPatch,
    getPendingAttributesPatchSync: getPendingAttributesPatchSync,
    markFlushed: markFlushed,
    clearAll: clearAll,
    computeCheckoutAttributes: computeCheckoutAttributes,
  }
})()
